import { useState } from 'react';
import { ClipboardDocumentCheckIcon, CheckBadgeIcon, WrenchScrewdriverIcon, CurrencyDollarIcon, ChevronDownIcon, ArrowLongRightIcon, PhoneIcon } from '@heroicons/react/24/solid';
import type { ComponentType, SVGProps } from 'react';
import { useContent } from '../context/ContentContext';

const STEP_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [
  ClipboardDocumentCheckIcon, CheckBadgeIcon, WrenchScrewdriverIcon, CurrencyDollarIcon,
];

export default function FinancingHowItWorks() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { content, settings } = useContent();
  const d = content.financingHowItWorks;

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-white">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 xl:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-[220px] xl:top-[240px] 2xl:top-[310px]">
            <span className="inline-block text-brand-orange font-semibold text-sm uppercase tracking-wider mb-3">
              {d.sectionLabel}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-[42px] lg:leading-[1.15] font-serif font-bold text-brand-navy mb-4 sm:mb-5">
              {d.heading}
            </h2>
            <p className="text-brand-warm-gray text-base sm:text-lg leading-relaxed mb-6">
              {d.description}
            </p>
            <a
              href="https://apply.optimusfinancing.com/EGIA-488076"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2.5 bg-brand-orange text-white px-6 py-3.5 rounded-lg font-semibold text-[15px] sm:text-base hover:bg-brand-navy transition-all duration-300"
            >
              {d.buttonLabel}
              <ArrowLongRightIcon className="w-5 h-5" />
            </a>
          </div>

          <div className="lg:col-span-7 space-y-4 sm:space-y-5">
            {d.steps.map((step, index) => {
              const isOpen = openIndex === index;
              const Icon = STEP_ICONS[index] || ClipboardDocumentCheckIcon;
              const isPhone = !step.ctaLink || step.ctaLink === '';
              const isExternal = step.ctaLink.startsWith('http');

              return (
                <div
                  key={index}
                  className={`group rounded-2xl border transition-all duration-300 ${
                    isOpen
                      ? 'bg-brand-off-white border-brand-orange/25 shadow-md'
                      : 'bg-brand-off-white border-brand-light-gray/60 hover:border-brand-orange/20 hover:shadow-md'
                  }`}
                >
                  <button
                    onClick={() => toggle(index)}
                    className="w-full flex items-start gap-5 sm:gap-6 p-5 sm:p-6 xl:p-7 text-left cursor-pointer"
                  >
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                        isOpen ? 'bg-brand-orange' : 'bg-brand-navy group-hover:bg-brand-orange'
                      }`}>
                        <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <span className="text-xs font-bold text-brand-warm-gray/50 uppercase tracking-wider">
                        Step {index + 1}
                      </span>
                    </div>
                    <div className="pt-1 flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-bold text-brand-navy mb-1.5">{step.title}</h3>
                      <p className="text-brand-warm-gray text-sm sm:text-base leading-relaxed">{step.description}</p>
                    </div>
                    <div className="flex-shrink-0 pt-2">
                      <ChevronDownIcon
                        className={`w-5 h-5 text-brand-warm-gray/40 transition-transform duration-300 ${
                          isOpen ? 'rotate-180 text-brand-orange' : ''
                        }`}
                      />
                    </div>
                  </button>

                  <div
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 sm:px-6 xl:px-7 pb-5 sm:pb-6 xl:pb-7 pl-[4.5rem] sm:pl-[5.25rem] xl:pl-[5.75rem]">
                        <div className="border-t border-brand-light-gray/40 pt-4 sm:pt-5">
                          <p className="text-brand-warm-gray text-sm sm:text-base leading-relaxed mb-5">
                            {step.details}
                          </p>
                          {isPhone ? (
                            <a
                              href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                              className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-brand-orange transition-all duration-300"
                            >
                              <PhoneIcon className="w-4 h-4" />
                              {step.ctaLabel}
                            </a>
                          ) : (
                            <a
                              href={step.ctaLink}
                              target={isExternal ? '_blank' : undefined}
                              rel={isExternal ? 'noopener noreferrer' : undefined}
                              className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-brand-orange transition-all duration-300"
                            >
                              {step.ctaLabel}
                              <ArrowLongRightIcon className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
